import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ScissorsIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/solid";
import { getHarvestCandidates } from "../../api/taxApi";
import AskCouncilButton from "../../components/AskCouncilButton";
import FeatureGate from "../../components/gating/FeatureGate";
import { AuthContext } from "../../context/AuthContext";
import { formatCurrency } from "../../utils/currency";

/**
 * Tax-loss harvest page — turns Tally's chat answer into a real, scannable
 * action page. Shows every position currently sitting at an unrealized
 * loss with an estimated tax saving if realised, scoped to the user's
 * jurisdiction.
 *
 * The "I sold this" action is intentionally per-session UI only — we don't
 * track "what the user actually did" because that's a tax-software concern
 * we don't claim to be.
 */
const TaxHarvest = () => {
  const { profile } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getHarvestCandidates();
        if (alive) setData(res);
      } catch {
        if (alive) setData({ candidates: [], totals: { absolute_loss: 0, estimated_tax_saving: 0 } });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const visible = useMemo(
    () => (data?.candidates || []).filter((c) => !dismissed.has(c.symbol)),
    [data, dismissed],
  );

  const visibleTotals = useMemo(() => {
    if (!visible.length) return { loss: 0, savings: 0 };
    return visible.reduce(
      (acc, c) => ({
        loss: acc.loss + (c.absolute_loss || 0),
        savings: acc.savings + (c.estimated_tax_saving || 0),
      }),
      { loss: 0, savings: 0 },
    );
  }, [visible]);

  const noJurisdiction = data && !data.jurisdiction;

  return (
    <div className="space-y-6 animate-fadeIn pt-4">
      <div>
        <Link
          to="/portfolio/holdings"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-500 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to Holdings
        </Link>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/15 text-amber-500 flex items-center justify-center shrink-0">
            <ScissorsIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold">Tax-loss harvest</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
              Positions sitting at an unrealized loss. Selling realises the loss; that loss
              can offset gains — saving you tax. Tally finds the candidates; this page does
              the back-of-envelope savings math against{" "}
              <span className="font-semibold">
                {data?.jurisdiction_label || "your jurisdiction"}
              </span>
              's rules.
            </p>
          </div>
        </div>
      </div>

      {noJurisdiction ? (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-600 dark:text-amber-400">
              No tax jurisdiction set
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              We're using a generic 20% rate. Set your{" "}
              <Link to="/settings?tab=investing" className="underline font-semibold">
                tax jurisdiction in Settings
              </Link>{" "}
              for a more accurate estimate.
            </p>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="p-10 rounded-2xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto" />
        </div>
      ) : visible.length === 0 ? (
        <div className="p-10 rounded-2xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 text-center">
          <CheckCircleIcon className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <h3 className="font-bold mb-1">Nothing to harvest</h3>
          <p className="text-sm text-gray-500">
            No position is currently at an unrealized loss. Come back when something dips.
          </p>
        </div>
      ) : (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-2xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                Candidates
              </p>
              <p className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white mt-1">
                {visible.length}
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                Realisable losses
              </p>
              <p className="text-2xl sm:text-3xl font-extrabold text-red-500 mt-1">
                {formatCurrency(visibleTotals.loss, profile?.display_currency || "USD")}
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-amber-500/10 border border-amber-500/40">
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400">
                Estimated tax saving
              </p>
              <p className="text-2xl sm:text-3xl font-extrabold text-amber-500 mt-1">
                ~{formatCurrency(visibleTotals.savings, profile?.display_currency || "USD")}
              </p>
              <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-1">
                Applied rate: {Math.round((data?.applied_rate || 0.2) * 100)}%
              </p>
            </div>
          </div>

          {data?.rate_note ? (
            <div className="p-3 rounded-lg bg-gray-100 dark:bg-white/5 text-xs text-gray-600 dark:text-gray-400">
              ↳ {data.rate_note}
            </div>
          ) : null}

          {/* Table */}
          <div className="p-4 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest border-b border-gray-200 dark:border-white/10">
                    <th className="text-left py-3 px-2">Position</th>
                    <th className="text-right py-3 px-2 hidden md:table-cell">Value</th>
                    <th className="text-right py-3 px-2 hidden lg:table-cell">Cost basis</th>
                    <th className="text-right py-3 px-2">Loss</th>
                    <th className="text-right py-3 px-2">Tax saving (est)</th>
                    <th className="text-right py-3 px-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                  {visible.map((c) => (
                    <tr key={c.symbol}>
                      <td className="py-3 px-2">
                        <div className="font-bold tracking-wide">{c.symbol}</div>
                        <div className="text-[11px] text-gray-500 truncate max-w-[180px]">
                          {c.name}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-right tabular-nums hidden md:table-cell">
                        {formatCurrency(c.marketValue, c.currency)}
                      </td>
                      <td className="py-3 px-2 text-right tabular-nums text-gray-500 hidden lg:table-cell">
                        {formatCurrency(c.costBasis, c.currency)}
                      </td>
                      <td className="py-3 px-2 text-right tabular-nums text-red-500 font-semibold">
                        −{formatCurrency(c.absolute_loss, c.currency)}
                        <div className="text-[10px] text-red-400 font-normal">
                          {(c.unrealizedPLPercent || 0).toFixed(2)}%
                        </div>
                      </td>
                      <td className="py-3 px-2 text-right tabular-nums text-amber-500 font-bold">
                        ~{formatCurrency(c.estimated_tax_saving, c.currency)}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <AskCouncilButton
                            variant="icon"
                            prefill={`Tally — should I harvest the loss on ${c.symbol} (${c.name})? It's currently ${formatCurrency(c.marketValue, c.currency)} value, ${formatCurrency(c.costBasis, c.currency)} cost basis, sitting at ${(c.unrealizedPLPercent || 0).toFixed(2)}%. Walk me through the tax + reinvestment + wash-sale implications for my jurisdiction.`}
                          />
                          <button
                            onClick={() =>
                              setDismissed((d) => new Set(d).add(c.symbol))
                            }
                            className="text-[11px] px-2 py-1 rounded-md text-gray-500 hover:text-emerald-500 hover:bg-emerald-500/10"
                            title="Hide from this view"
                          >
                            I sold this
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-[11px] text-gray-500">
            Estimates only — actual savings depend on your bracket, holding period, and
            other realised gains/losses this year. Tally (in chat) handles the nuance.
          </p>
        </>
      )}
    </div>
  );
};

const TaxHarvestGated = () => (
  <FeatureGate feature="tax_harvest">
    <TaxHarvest />
  </FeatureGate>
);

export default TaxHarvestGated;
